import { Offer, Address } from '../types/offers';
import { trackOfferActionTapped } from '../utils/nativeBridge';
import backIcon from '../assets/icons/back.svg';
import { formatReward } from '../utils/rewardFormatter';
import { useSegmentTracking, EVENTS } from '../hooks/useSegmentTracking';

interface TransactionDetailsProps {
  minTransaction?: { value: number };
  maxTransaction?: { value: number };
  minReward?: { value: number };
  maxReward?: { value: number };
}

function TransactionDetails({
  minTransaction,
  maxTransaction,
  minReward,
  maxReward
}: TransactionDetailsProps) {
  if (!minTransaction && !maxTransaction && !minReward && !maxReward) {
    return null;
  }

  const formatAmount = (cents: number) => {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  };

  return (
    <>
      <div className="border-t border-gray-200 my-4"></div>

      {minTransaction && (
        <div className="text-gray-600 paragraph-small">
          Min transaction amount: {formatAmount(minTransaction.value)}
        </div>
      )}

      {maxTransaction && (
        <div className="text-gray-600 paragraph-small">
          Max transaction amount: {formatAmount(maxTransaction.value)}
        </div>
      )}

      {minReward && (
        <div className="text-gray-600 paragraph-small">
          Min reward amount: {formatAmount(minReward.value)}
        </div>
      )}

      {maxReward && (
        <div className="text-gray-600 paragraph-small">
          Max reward amount: {formatAmount(maxReward.value)}
        </div>
      )}
    </>
  );
}

const formatFullAddress = (location: Address): { line1: string; line2: string } => {
  const line1 = location.street || '';
  const parts: string[] = [];

  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.zip_code) parts.push(location.zip_code);

  const line2 = parts.join(', ');

  return { line1, line2 };
};

interface OfferDetailProps {
  offer: Offer;
  onBack: () => void;
}

export default function OfferDetail({ offer, onBack }: OfferDetailProps) {
  const { track } = useSegmentTracking();
  const { attributes } = offer;
  const logoImage = attributes.assets.find(asset => asset.type === 'IMG_VIEW');

  // Format expiration date
  const expirationDate = new Date(attributes.expiration_date);
  const formattedDate = expirationDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Get purchase channel text
  const getPurchaseChannelText = () => {
    const hasOnline = attributes.purchase_channel.includes('ONLINE');
    const hasInstore = attributes.purchase_channel.includes('INSTORE');

    if (hasOnline && hasInstore) {
      return 'Online and in-store';
    } else if (hasOnline) {
      return 'Online only';
    } else if (hasInstore) {
      return 'In-store only';
    }
    return 'Online and in-store'; // Default case
  };

  const handleShopNow = () => {
    trackOfferActionTapped(offer);
    track(EVENTS.ButtonClicked, {
      id: 'shop-now-btn',
      offerName: offer.attributes.name,
      offerRewardType: offer.attributes.user_reward.type,
      offerRewardValue: offer.attributes.user_reward.value,
    });
  };

  return (
    <div className="fixed inset-0 bg-white overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white px-4 py-4 flex items-center">
        <button onClick={onBack} className="p-1 back-button" aria-label="Back">
          <img src={backIcon} alt="Back" className="h-6 w-6" />
        </button>
        <div className="w-6"></div> {/* Spacer for alignment */}
      </div>

      {/* Centered Logo and Brand Information */}
      <div className="flex flex-col items-center pt-8 pb-6">
        {logoImage ? (
          <img
            src={logoImage.url + (logoImage.url.includes('?') ? '&' : '?') + 'eventCode=VIEW'}
            alt={logoImage.alt || attributes.name}
            className="w-24 h-24 rounded-full object-contain"
          />
        ) : (
          <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-2xl font-bold text-gray-500">{attributes.name.charAt(0)}</span>
          </div>
        )}
        <div className="w-full text-center px-4">
          <h1 className="heading-large mt-6 mb-2">{attributes.name}</h1>
          <p className="heading-medium">{formatReward(attributes.user_reward)}</p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="px-4">
        <div className="rounded-background space-y-4">
          <div className="flex items-center">
            <div className="mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <span className="text-gray-800 paragraph-medium">{getPurchaseChannelText()}</span>
          </div>

          {attributes.location && (
            <div className="flex items-start">
              <div className="mr-4 mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-gray-800 paragraph-medium">
                {(() => {
                  const { line1, line2 } = formatFullAddress(attributes.location);
                  return (
                    <>
                      {line1 && <div>{line1}</div>}
                      {line2 && <div>{line2}</div>}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="flex items-center">
            <div className="mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <span className="text-gray-800 paragraph-medium">Pay with your credit card</span>
          </div>

          <div className="flex items-center">
            <div className="mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-gray-800 paragraph-medium">Expires {formattedDate}</span>
          </div>

          {/* Transaction and Reward Details */}
          <TransactionDetails
            minTransaction={attributes.min_transaction_amount}
            maxTransaction={attributes.max_transaction_amount}
            minReward={attributes.min_reward_amount}
            maxReward={attributes.max_reward_amount}
          />
        </div>

        {/* Shop Now Button */}
        {attributes.purchase_channel.includes('ONLINE') && attributes.website_url && (
          <a
            href={attributes.website_url}
            onClick={handleShopNow}
            className="w-full px-4 py-3 bg-black text-white rounded-full label-medium mb-6 block text-center uppercase"
          >
            Shop now
          </a>
        )}

        {/* Terms */}
        <div className="text-[#74767B] paragraph-small mb-6 text-center">

        <p >
        Cash back is earned on top of your existing credit card rewards and credited once eligible transactions settle.
        </p>
        <br />
        <p >
          {attributes.terms}
        </p>
        </div>

      </div>
    </div>
  );
}